import { useState, useCallback, useEffect, useRef, type FC } from "react";
import { X, Upload, Circle, ChevronDown, Loader2, Plus } from "lucide-react";
import { format, parseISO } from "date-fns";
import { enqueueSnackbar } from "notistack";

import { cn } from "@/shared/utils/core";
import { Button } from "@/shared/components/ui/button";
import { Textarea } from "@/shared/components/ui/textarea";
import { formatTransactionAmount } from "@/shared/utils/formatters";
import { uploadDocumentWithAuth } from "@/shared/utils/uploadSupportingDocument";
import { FileCard } from "@/shared/components/ui/file-card";

import type { PlaidTransaction, TransactionUpdate } from "@/shared/hooks/useTransactions";
import { getCategoryConfig, CATEGORY_DOT_COLORS } from "@/pages/postloss/estate-inventory/constants/transactionCategories";
import { CategoryPicker } from "./CategoryPicker";
import { ChooseReceiptDialog } from "./ChooseReceiptDialog";

interface TransactionDetailPanelProps {
  transaction: PlaidTransaction | null;
  onClose: () => void;
  onUpdate: (transactionId: number, updates: TransactionUpdate) => Promise<PlaidTransaction>;
  isUpdating: boolean;
  estateId: number | null;
  disableCategory?: boolean;
}

const DetailRow: FC<{
  label: string;
  children: React.ReactNode;
  align?: "center" | "start";
}> = ({ label, children, align = "center" }) => (
  <div className={cn("flex flex-col gap-1 sm:flex-row", align === "start" ? "sm:items-start" : "sm:items-center")}>
    <span className="shrink-0 text-[13px] text-secondary sm:w-[250px]">{label}</span>
    <div className="flex min-w-0 flex-1 items-center gap-2 text-sm font-medium">{children}</div>
  </div>
);

function formatMethod(transaction: PlaidTransaction): string | null {
  if (transaction.check_number) return `Check #${transaction.check_number}`;
  if (transaction.payment_channel === "online") return "Online";
  if (transaction.payment_channel === "in store") return "In store";
  if (transaction.payment_channel) return transaction.payment_channel;
  return null;
}

function formatFullDate(dateStr: string | null): string | null {
  if (!dateStr) return null;
  return format(parseISO(dateStr), "MMMM d, yyyy");
}

export const TransactionDetailPanel: FC<TransactionDetailPanelProps> = ({
  transaction,
  onClose,
  onUpdate,
  estateId,
  disableCategory = false,
}) => {
  const [visible, setVisible] = useState(false);
  const [notes, setNotes] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [showDocPicker, setShowDocPicker] = useState(false);
  const notesTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (transaction) {
      requestAnimationFrame(() => setVisible(true));
      document.body.style.overflow = "hidden";
    } else {
      setVisible(false);
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [transaction]);

  useEffect(() => {
    if (transaction) {
      setNotes(transaction.notes ?? "");
    }
  }, [transaction?.id]);

  useEffect(() => {
    return () => {
      if (notesTimerRef.current) clearTimeout(notesTimerRef.current);
    };
  }, []);

  const handleNotesChange = useCallback(
    (value: string) => {
      setNotes(value);
      if (!transaction) return;
      if (notesTimerRef.current) clearTimeout(notesTimerRef.current);
      notesTimerRef.current = setTimeout(() => {
        onUpdate(transaction.id, { notes: value || null });
      }, 1000);
    },
    [transaction, onUpdate],
  );

  const handleClose = useCallback(async () => {
    if (notesTimerRef.current) {
      clearTimeout(notesTimerRef.current);
      notesTimerRef.current = null;
      if (transaction && notes !== (transaction.notes ?? "")) {
        try {
          await onUpdate(transaction.id, { notes: notes || null });
        } catch {
          enqueueSnackbar("Couldn't save note. Please try again.", { variant: "error" });
          return;
        }
      }
    }
    onClose();
  }, [transaction, notes, onUpdate, onClose]);

  const handleCategoryChange = useCallback(
    (category: string) => {
      if (!transaction) return;
      onUpdate(transaction.id, { category });
    },
    [transaction, onUpdate],
  );

  const handleReceiptUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || !transaction || !estateId) return;
      e.target.value = "";
      setIsUploading(true);
      try {
        const documentId = await uploadDocumentWithAuth(file, estateId, "payment_receipt");
        await onUpdate(transaction.id, { receipt_document_id: documentId, has_receipt: true });
        enqueueSnackbar("Receipt uploaded", { variant: "success" });
      } catch {
        enqueueSnackbar("Couldn't upload receipt. Please try again.", { variant: "error" });
      } finally {
        setIsUploading(false);
      }
    },
    [transaction, estateId, onUpdate],
  );

  const handleAttachDocument = useCallback(
    async (documentId: number) => {
      if (!transaction) return;
      try {
        await onUpdate(transaction.id, { receipt_document_id: documentId, has_receipt: true });
        setShowDocPicker(false);
        enqueueSnackbar("Receipt attached", { variant: "success" });
      } catch {
        enqueueSnackbar("Couldn't attach receipt. Please try again.", { variant: "error" });
      }
    },
    [transaction, onUpdate],
  );

  const handleRemoveReceipt = useCallback(async () => {
    if (!transaction) return;
    await onUpdate(transaction.id, { receipt_document_id: null, has_receipt: false });
  }, [transaction, onUpdate]);

  if (!transaction && !visible) return null;

  const categoryConfig = transaction
    ? getCategoryConfig(transaction.category, transaction.is_inflow, transaction.description, transaction.original_description)
    : null;
  const method = transaction ? formatMethod(transaction) : null;
  const subtitle = transaction
    ? [format(parseISO(transaction.date), "MMM d, yyyy"), method].filter(Boolean).join(" \u00B7 ")
    : "";
  const amountFormatted = transaction
    ? formatTransactionAmount(transaction.amount, transaction.is_inflow)
    : "";

  return (
    <>
      <div
        className={cn(
          "fixed inset-0 z-40 bg-black/50 transition-opacity duration-300",
          visible ? "opacity-100" : "opacity-0 pointer-events-none",
        )}
        onClick={handleClose}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-label="Transaction details"
        aria-modal="true"
        className={cn(
          "fixed inset-0 z-50 flex flex-col bg-white shadow-lg transition-transform duration-300 ease-in-out",
          "sm:left-auto sm:right-0 sm:top-0 sm:bottom-0 sm:w-[800px]",
          visible ? "translate-x-0" : "translate-x-full",
        )}
      >
        <div className="flex flex-1 flex-col overflow-y-auto">
          {transaction && (
            <>
              {/* Header */}
              <div className="flex items-center gap-4 px-4 py-6 sm:px-6">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleClose}
                  aria-label="Close panel"
                >
                  <X className="h-5 w-5 text-secondary" />
                </Button>
                <div className="flex flex-1 flex-col gap-1 min-w-0">
                  <h4 className="truncate leading-[30px]">
                    {transaction.counterparty_name ?? transaction.description}
                  </h4>
                  <p className="text-sm text-secondary">{subtitle}</p>
                </div>
                <p className={cn(
                  "shrink-0 text-xl font-semibold leading-[30px]",
                  transaction.is_inflow ? "text-brand" : "text-primary",
                )}>
                  {amountFormatted}
                </p>
              </div>

              <hr className="mx-4 border-primary sm:mx-6" />

              {/* Transaction details */}
              <div className="flex flex-col gap-4 px-4 py-6 sm:px-6">
                <h4>Transaction details</h4>
                <div className="flex flex-col gap-4">
                  {transaction.counterparty_name ? (
                    <DetailRow label="Paid to">
                      <span>{transaction.counterparty_name}</span>
                    </DetailRow>
                  ) : null}
                  <DetailRow label="Status">
                    <Circle
                      className={cn(
                        "h-1.5 w-1.5 shrink-0",
                        transaction.pending
                          ? "fill-yellow-400 text-yellow-400"
                          : "fill-green-500 text-green-500",
                      )}
                    />
                    <span>{transaction.pending ? "Pending" : "Posted"}</span>
                  </DetailRow>
                  {transaction.authorized_date ? (
                    <DetailRow label="Written">
                      <span>{formatFullDate(transaction.authorized_date)}</span>
                    </DetailRow>
                  ) : null}
                  <DetailRow label="Cleared">
                    <span>{formatFullDate(transaction.date)}</span>
                  </DetailRow>
                  {method ? (
                    <DetailRow label="Method">
                      <span>{method}</span>
                    </DetailRow>
                  ) : null}
                  {transaction.reference_number ? (
                    <DetailRow label="Reference">
                      <span>{transaction.reference_number}</span>
                    </DetailRow>
                  ) : null}
                  {transaction.original_description ? (
                    <DetailRow label="Bank statement text">
                      <span>{transaction.original_description}</span>
                    </DetailRow>
                  ) : null}
                </div>
              </div>

              <hr className="mx-4 border-primary sm:mx-6" />

              {/* Your details */}
              <div className="flex flex-col gap-4 px-4 py-6 sm:px-6">
                <h4>Your details</h4>
                {!disableCategory && (
                <DetailRow label="Category">
                  <CategoryPicker
                    isInflow={transaction.is_inflow}
                    onSelect={handleCategoryChange}
                  >
                    {categoryConfig ? (
                      <button
                        type="button"
                        className="flex h-10 w-full items-center justify-between rounded-sm border border-primary bg-white px-3 sm:w-[300px]"
                      >
                        <span className="flex items-center gap-2">
                          <span className={cn("h-2 w-2 shrink-0 rounded-full", CATEGORY_DOT_COLORS[categoryConfig.color] ?? "bg-stone-400")} />
                          <span className="text-sm">{categoryConfig.label}</span>
                        </span>
                        <ChevronDown className="h-3 w-3 text-secondary" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="flex h-10 w-full items-center justify-between rounded-sm border border-dashed border-strong bg-white px-3 text-sm text-secondary hover:border-stone-400 transition-colors sm:w-[300px]"
                      >
                        <span>+ Add category</span>
                        <ChevronDown className="h-3 w-3 text-secondary" />
                      </button>
                    )}
                  </CategoryPicker>
                </DetailRow>
                )}
                <DetailRow label="Notes" align="start">
                  <Textarea
                    value={notes}
                    onChange={(e) => handleNotesChange(e.target.value)}
                    placeholder="What was this for? Add a case number, invoice number, or anything else important to note."
                    className="min-h-[72px]"
                  />
                </DetailRow>
              </div>

              <hr className="mx-4 border-primary sm:mx-6" />

              {/* Receipt */}
              <div className="flex flex-col gap-4 px-4 py-6 sm:px-6">
                <h4>Receipt</h4>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.heic,.heif"
                  className="hidden"
                  onChange={handleReceiptUpload}
                />
                {transaction.has_receipt ? (
                  <>
                    <FileCard
                      fileName={`Receipt of ${transaction.counterparty_name ?? transaction.description} (${categoryConfig?.label ?? "Uncategorized"}).pdf`}
                      onRemove={handleRemoveReceipt}
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploading}
                      className="flex items-center gap-1 text-sm font-medium text-brand hover:underline self-start"
                    >
                      {isUploading ? "Uploading..." : "Add another file"}
                      {isUploading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Plus className="h-4 w-4" />
                      )}
                    </button>
                  </>
                ) : (
                  <div className="flex flex-col gap-4 rounded-lg border border-dashed border-strong p-4 sm:flex-row sm:items-center sm:gap-3 sm:p-6">
                    <div className="flex flex-1 items-center gap-3 min-w-0">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-background">
                        <Upload className="h-4 w-4 text-secondary" />
                      </div>
                      <div className="flex flex-col gap-0.5 min-w-0">
                        <p className="text-sm font-semibold">No receipt yet</p>
                        <p className="text-sm text-secondary">Upload the bill or a photo of it here</p>
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button
                        variant="brand"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isUploading}
                      >
                        {isUploading ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          "Add receipt"
                        )}
                      </Button>
                      <Button variant="outline" onClick={() => setShowDocPicker(true)}>
                        Choose from Documents
                      </Button>
                    </div>
                  </div>
                )}
              </div>

            </>
          )}
        </div>
      </div>
      {transaction && (
        <ChooseReceiptDialog
          open={showDocPicker}
          onOpenChange={setShowDocPicker}
          onSelect={handleAttachDocument}
          onUploadInstead={() => {
            setShowDocPicker(false);
            fileInputRef.current?.click();
          }}
          transactionDescription={transaction.counterparty_name ?? transaction.description}
          transactionAmount={amountFormatted}
          transactionDate={format(parseISO(transaction.date), "MMM d, yyyy")}
        />
      )}
    </>
  );
};
